import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { saveToken, getToken } from '../utils/token';
import { popRedirectPath } from '../utils/redirectAfterLogin';
import Toast from '../components/Toast';

const SAVED_EMAIL_KEY = 'savedEmail';

export default function Login() {
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [toast, setToast]           = useState(null);
  const navigate = useNavigate();

  const showToast = (message, type = 'error') => setToast({ message, type });
  const clearToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    if (getToken()) {
      navigate('/home', { replace: true });
      return;
    }
    const saved = localStorage.getItem(SAVED_EMAIL_KEY);
    if (saved) {
      setEmail(saved);
      setRememberMe(true);
    }
  }, [navigate]);

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      showToast('Completá email y contraseña');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      if (rememberMe) {
        localStorage.setItem(SAVED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(SAVED_EMAIL_KEY);
      }
      saveToken(res.data.token);
      localStorage.setItem('userId', res.data.userId);
      localStorage.setItem('userName', res.data.name);
      try {
        const profile = await api.get('/users/me');
        const s = parseInt(profile.data.profilePicture);
        localStorage.setItem('avatarSeed', isNaN(s) ? res.data.userId : String(s));
      } catch { localStorage.setItem('avatarSeed', res.data.userId); }

      const pendingJoinCode = localStorage.getItem('pendingJoinCode');
      if (pendingJoinCode) {
        localStorage.removeItem('pendingJoinCode');
        try {
          await api.post(`/vaquita/join/${pendingJoinCode}`);
        } catch {
          // Puede que ya sea participante (ej. reintentos); igual lo llevamos a la vaquita.
        }
        navigate(`/vaquita/code/${pendingJoinCode}`);
        return;
      }

      navigate(popRedirectPath() || '/home');
    } catch {
      showToast('Email o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleLogin(); };

  return (
    <div className="login-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}
      <div className="login-card">
        <h2>🐮 VaquitApp 🐮</h2>
        <p className="welcome-text">
          ¡Bienvenido de vuelta! Inicia sesión para continuar con tus vaquitas
        </p>

        <div className="login-form">
          <div className="input-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              className="login-input"
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div className="input-group">
            <label>Contraseña</label>
            <input
              type="password"
              placeholder="Tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              className="login-input"
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <label className="remember-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            Recordar correo
          </label>

          <button onClick={handleLogin} className="login-button" disabled={loading}>
            {loading ? 'Ingresando...' : 'Ingresar 🐄'}
          </button>
        </div>

        <p className="register-link">
          ¿No tienes cuenta? <Link to="/register">Regístrate aquí</Link>
        </p>
      </div>

      <style jsx>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          background: var(--bg-primary);
        }

        .login-card {
          background: var(--bg-secondary);
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 4px 12px var(--shadow-color);
          width: 100%;
          max-width: 400px;
        }

        h2 {
          text-align: center;
          color: var(--text-primary);
          margin-bottom: 1rem;
          font-size: 1.8rem;
        }

        .welcome-text {
          text-align: center;
          color: var(--text-primary);
          margin-bottom: 1.5rem;
          font-size: 1rem;
          line-height: 1.5;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        label {
          color: var(--text-primary);
          font-size: 0.95rem;
          font-weight: 500;
        }

        .remember-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: normal;
          font-size: 0.9rem;
          cursor: pointer;
        }

        .login-input {
          padding: 0.8rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          font-size: 1rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          width: 100%;
        }

        .login-input:focus {
          outline: none;
          border-color: var(--accent-color);
          box-shadow: 0 0 0 3px var(--accent-color-light);
        }

        .login-button {
          background: var(--accent-color);
          color: white;
          border: none;
          padding: 1rem;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 500;
          cursor: pointer;
          width: 100%;
          margin-top: 0.3rem;
        }

        .login-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .register-link {
          text-align: center;
          margin-top: 1.5rem;
          color: var(--text-primary);
          font-size: 0.95rem;
        }

        .register-link a {
          color: var(--accent-color);
          text-decoration: none;
          font-weight: 500;
          margin-left: 0.3rem;
        }
      `}</style>
    </div>
  );
}
